/**
 * Portal Mode Hooks
 *
 * Centralizes portal-mode detection logic that is currently scattered
 * across many components. There are two layers of portal mode in Helix:
 *
 * 1. **User Company Type** (from AuthContext):
 *    The user's company is either 'customer', 'supplier', or 'helix'.
 *    Accessed via `user.company_type` (lowercase) or `user.platform` (uppercase).
 *    This is the fundamental role of the user and does not change during a session.
 *
 * 2. **View Context Portal Mode** (from ViewContext):
 *    For companies with `portal_mode === 'both'`, the user can toggle between
 *    customer and supplier views. This is the active viewing perspective.
 *
 * These hooks unify both concepts:
 * - `usePortalMode()` returns the full portal mode state
 * - `useIsSupplier()` returns true if the user is acting as a supplier
 * - `useIsCustomer()` returns true if the user is acting as a customer
 *
 * The hooks use the ViewContext portal mode when dual mode is supported,
 * falling back to the user's company type otherwise.
 *
 * Files that would benefit from adopting these hooks:
 * - frontend/src/pages/Purchasing.tsx
 * - frontend/src/pages/StockLevelsPage.tsx
 * - frontend/src/pages/Concessions.tsx
 * - frontend/src/pages/SupplierQualifications.tsx
 * - frontend/src/pages/QualificationDetail.tsx
 * - frontend/src/pages/ConcessionDetail.tsx
 * - frontend/src/pages/NCREdit.tsx
 * - frontend/src/pages/SupplierDetail.tsx
 * - frontend/src/pages/onboarding/OnboardingDashboard.tsx
 * - frontend/src/pages/qualification-detail/useQualificationDetail.ts
 * - frontend/src/pages/concession-detail/useConcessionDetail.ts
 * - frontend/src/pages/concessions-list/useConcessions.ts
 * - frontend/src/pages/supplier-detail/useSupplierDetail.ts
 * - frontend/src/pages/ncr-edit/useNCREdit.ts
 * - frontend/src/components/purchasing/SupplierQueryTab.tsx
 * - frontend/src/components/purchasing/POLineDetailModal.tsx
 * - frontend/src/components/purchasing/tabs/ASNTab.tsx
 * - frontend/src/components/purchasing/tabs/po-lines/usePOLinesState.ts
 * - frontend/src/components/tour/WelcomeModal.tsx
 * - frontend/src/components/tour/QuickReferenceModal.tsx
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useView, type PortalMode } from '../contexts/ViewContext';

/**
 * Determines the effective company type based on user data.
 *
 * The user object carries role information in two fields:
 * - `company_type`: lowercase ('customer' | 'supplier' | 'helix')
 * - `platform`: uppercase ('CUSTOMER' | 'SUPPLIER' | 'HELIX')
 *
 * Both represent the same concept; this normalizes them to lowercase.
 */
function resolveCompanyType(
  companyType?: 'customer' | 'supplier' | 'helix' | 'logistics',
  platform?: 'CUSTOMER' | 'SUPPLIER' | 'HELIX'
): 'customer' | 'supplier' | 'helix' | 'logistics' | undefined {
  if (companyType) return companyType;
  if (platform) return platform.toLowerCase() as 'customer' | 'supplier' | 'helix';
  return undefined;
}

export interface PortalModeState {
  /** The active portal mode from ViewContext ('customer' | 'supplier') */
  portalMode: PortalMode;

  /** Whether the user's company supports both customer and supplier views */
  supportsDualMode: boolean;

  /** The user's underlying company type (does not change with view switching) */
  companyType: 'customer' | 'supplier' | 'helix' | 'logistics' | undefined;

  /** True if the user is currently viewing as a supplier (via role or active view) */
  isSupplier: boolean;

  /** True if the user is currently viewing as a customer (includes helix admins) */
  isCustomer: boolean;

  /** True if the user belongs to the Helix admin platform */
  isHelix: boolean;
}

/**
 * Returns the complete portal mode state, combining the user's company type
 * with the active view context portal mode.
 *
 * For dual-mode companies, `isSupplier` and `isCustomer` reflect the
 * currently selected view rather than the underlying company type.
 *
 * Usage:
 * ```ts
 * const { isSupplier, isCustomer, portalMode } = usePortalMode();
 * ```
 */
export function usePortalMode(): PortalModeState {
  const { user } = useAuth();
  const { portalMode, supportsDualMode } = useView();

  return useMemo(() => {
    const companyType = resolveCompanyType(user?.company_type, user?.platform);

    // For dual-mode companies, defer to the active view context portal mode.
    // For single-mode companies, use the company type directly.
    const effectiveMode: PortalMode = supportsDualMode
      ? portalMode
      : (companyType === 'supplier' ? 'supplier' : 'customer');

    const isSupplier = effectiveMode === 'supplier';
    const isCustomer = effectiveMode === 'customer';
    const isHelix = companyType === 'helix';

    return {
      portalMode: effectiveMode,
      supportsDualMode,
      companyType,
      isSupplier,
      isCustomer,
      isHelix,
    };
  }, [user?.company_type, user?.platform, portalMode, supportsDualMode]);
}

/**
 * Returns true if the user is currently acting as a supplier.
 *
 * This checks the view context portal mode for dual-mode companies,
 * and falls back to the user's company type for single-mode companies.
 *
 * Replaces scattered patterns like:
 * ```ts
 * const isSupplier = user?.company_type === 'supplier';
 * const isSupplier = user?.platform === 'SUPPLIER';
 * ```
 */
export function useIsSupplier(): boolean {
  const { isSupplier } = usePortalMode();
  return isSupplier;
}

/**
 * Returns true if the user is currently acting as a customer.
 *
 * This includes Helix admin users, matching the existing convention where
 * `company_type === 'helix'` is treated as a customer-like role.
 *
 * Replaces scattered patterns like:
 * ```ts
 * const isCustomer = user?.company_type === 'customer' || user?.company_type === 'helix';
 * const isCustomer = user?.platform === 'CUSTOMER' || user?.platform === 'HELIX';
 * ```
 */
export function useIsCustomer(): boolean {
  const { isCustomer, isHelix } = usePortalMode();
  return isCustomer || isHelix;
}
